import React, { useState, useEffect, Component, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { HarmonyLogoCompact } from '../components/HarmonyLogo';
import { GrowthProvider, useGrowth } from '../context/GrowthContext';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  LayoutDashboard, StickyNote, ShieldCheck, Trophy,
  ChevronDown, LogOut, AlertTriangle, RefreshCw, ClipboardList,
  PenLine, Settings,
} from 'lucide-react';
import usePageMeta from '../hooks/usePageMeta';

// Code-split each tab so the initial bundle is just the shell.
const GrowthDashboard = lazy(() => import('../components/growth/GrowthDashboard'));
const GrowthNotes = lazy(() => import('../components/growth/GrowthNotes'));
const GrowthProbation = lazy(() => import('../components/growth/GrowthProbation'));
const GrowthAwards = lazy(() => import('../components/growth/GrowthAwards'));
const GrowthCycles = lazy(() => import('../components/growth/GrowthCycles'));
const GrowthMyReviews = lazy(() => import('../components/growth/GrowthMyReviews'));
// Resources + Framework live behind one Settings tab — see GrowthSettings.
const GrowthSettings = lazy(() => import('../components/growth/GrowthSettings'));
const GrowthReport = lazy(() => import('../components/growth/GrowthReport'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

class GrowthErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Harmony Growth crashed:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="text-center max-w-md space-y-3">
            <AlertTriangle className="w-10 h-10 text-destructive mx-auto" />
            <h1 className="text-xl font-semibold">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">
              {this.state.error?.message || 'An unexpected error occurred.'}
            </p>
            <Button onClick={() => window.location.reload()}>Reload</Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// `reviewerTab` marks the one tab a reviewer with no other Growth access may
// see. Everything else is hidden from them entirely.
const TABS = [
  { key: 'myreviews', label: 'My Reviews', icon: PenLine,         adminOnly: false, reviewerTab: true },
  { key: 'dashboard', label: 'Dashboard',  icon: LayoutDashboard, adminOnly: false },
  { key: 'notes',     label: 'Notes',      icon: StickyNote,      adminOnly: false },
  { key: 'cycles',    label: 'Reviews',    icon: ClipboardList,   adminOnly: false },
  { key: 'probation', label: 'Probation',  icon: ShieldCheck,     adminOnly: false },
  { key: 'awards',    label: 'Awards',     icon: Trophy,          adminOnly: false },
  { key: 'settings',  label: 'Settings',   icon: Settings,        adminOnly: true },
];

const GrowthContent = () => {
  usePageMeta('Harmony Growth | Harmony Apps', '/favicon-growth.svg');

  const [searchParams, setSearchParams] = useSearchParams();
  const { userName, role, isAdmin, reviewerOnly, bootError } = useGrowth();

  const tabFromUrl = searchParams.get('tab') || (reviewerOnly ? 'myreviews' : 'dashboard');
  const [activeTab, setActiveTab] = useState(tabFromUrl);

  useEffect(() => {
    if (tabFromUrl !== activeTab) setActiveTab(tabFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFromUrl]);

  const handleTabChange = (next) => {
    setActiveTab(next);
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const getInitials = (name) =>
    (name || 'U').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

  // isAdmin === null means the access check hasn't come back yet. Render a
  // loader rather than flashing admin-only tabs at someone who can't use them.
  if (isAdmin === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-6 h-6 animate-spin text-emerald-600" />
          <p className="text-sm">Loading Harmony Growth…</p>
        </div>
      </div>
    );
  }

  if (bootError === 'forbidden') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="text-center max-w-md space-y-3">
          <ShieldCheck className="w-10 h-10 text-muted-foreground mx-auto" />
          <h1 className="text-xl font-semibold">No access</h1>
          <p className="text-sm text-muted-foreground">
            Harmony Growth is available to HR, leadership and reviewers only.
            If you believe you should have access, contact HR.
          </p>
        </div>
      </div>
    );
  }

  // A reviewer who only holds an assignment sees exactly one tab.
  const visibleTabs = reviewerOnly
    ? TABS.filter((t) => t.reviewerTab)
    : TABS.filter((t) => !t.adminOnly || isAdmin);

  // The consolidated report is a full-screen view rather than a tab — it is
  // always opened for ONE cycle, so it is addressed by ?tab=reports&cycle_id=…
  // which keeps it deep-linkable and survives a refresh mid-authoring.
  const reportCycleId = searchParams.get('cycle_id');
  const showReport = activeTab === 'reports' && !!reportCycleId && !reviewerOnly;

  const safeTab = showReport || visibleTabs.some((t) => t.key === activeTab)
    ? activeTab
    : (visibleTabs[0]?.key || 'myreviews');

  const closeReport = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('cycle_id');
    params.set('tab', 'cycles');
    setSearchParams(params, { replace: true });
    setActiveTab('cycles');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="growth-app">
      <nav className="sticky top-0 z-50 bg-white border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <HarmonyLogoCompact size={32} />
              <div>
                <h1 className="font-semibold text-lg text-foreground">Harmony Growth</h1>
                <p className="text-xs text-muted-foreground">Harmony Apps</p>
              </div>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="growth-user-menu">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-emerald-600 text-white text-sm">
                      {getInitials(userName)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium">{userName}</span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>
                  <p className="text-sm font-medium">{userName}</p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {(role || '').replace('_', ' ')}
                  </p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => window.close()} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Close App
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="pb-2 -mx-4 px-4 overflow-x-auto">
            <Tabs value={safeTab} onValueChange={handleTabChange}>
              <TabsList>
                {visibleTabs.map((t) => {
                  const Icon = t.icon;
                  return (
                    <TabsTrigger key={t.key} value={t.key} data-testid={`growth-tab-${t.key}`}>
                      <Icon className="w-4 h-4 mr-2" />
                      {t.label}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </Tabs>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Suspense
          fallback={
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              Loading…
            </div>
          }
        >
          {showReport ? (
            <GrowthReport cycleId={reportCycleId} onClose={closeReport} />
          ) : (
            <>
              {safeTab === 'myreviews' && <GrowthMyReviews />}
              {safeTab === 'dashboard' && !reviewerOnly && <GrowthDashboard onJumpToTab={handleTabChange} />}
              {safeTab === 'notes' && !reviewerOnly && <GrowthNotes />}
              {safeTab === 'cycles' && !reviewerOnly && <GrowthCycles />}
              {safeTab === 'probation' && !reviewerOnly && <GrowthProbation />}
              {safeTab === 'awards' && !reviewerOnly && <GrowthAwards />}
              {safeTab === 'settings' && isAdmin && (
                <GrowthSettings
                  initialSection={searchParams.get('section') || undefined}
                  onSectionChange={(section) => {
                    const params = new URLSearchParams(searchParams);
                    if (section && section !== 'resources') params.set('section', section);
                    else params.delete('section');
                    setSearchParams(params, { replace: true });
                  }}
                />
              )}
            </>
          )}
        </Suspense>
      </main>
    </div>
  );
};

// Best-effort JWT payload decode — fallback only, used when /auth/me errors.
const _peekJwt = (jwt) => {
  try {
    const [, payload] = (jwt || '').split('.');
    if (!payload) return null;
    const base = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base + '='.repeat((4 - base.length % 4) % 4)));
  } catch (_) { return null; }
};

const GrowthApp = () => {
  // Same bootstrap as Harmony Timesheet: no token in the URL. The HttpOnly
  // refresh cookie set at shell login rides along on a same-origin request,
  // and we trade it for a short-lived access token held in memory only.
  const [bootState, setBootState] = useState({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const refresh = await axios.post(
          `${BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true },
        );
        const token = refresh.data?.token;
        if (!token) throw new Error('No token in refresh response');

        let userName = 'User';
        let role = 'viewer';
        let userId = null;
        try {
          const me = await axios.get(`${BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          userName = me.data?.full_name || me.data?.username || userName;
          role = me.data?.role || role;
          userId = me.data?.id || null;
        } catch (_) {
          const claims = _peekJwt(token);
          if (claims?.username) userName = claims.username;
          if (claims?.role) role = claims.role;
          if (claims?.sub) userId = claims.sub;
        }

        if (!cancelled) setBootState({ status: 'ready', token, userName, role, userId });
      } catch (e) {
        if (!cancelled) setBootState({ status: 'unauthenticated' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (bootState.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-6 h-6 animate-spin text-emerald-600" />
          <p className="text-sm">Signing in…</p>
        </div>
      </div>
    );
  }

  if (bootState.status === 'unauthenticated') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-destructive mb-2">Session expired</h1>
          <p className="text-muted-foreground mb-4">
            Please sign in to the Harmony Apps dashboard, then launch Harmony Growth again.
          </p>
          <Button onClick={() => { window.location.href = `${window.location.origin}/login`; }}>
            Go to sign-in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <GrowthErrorBoundary>
      <GrowthProvider
        token={bootState.token}
        role={bootState.role}
        userName={bootState.userName}
        userId={bootState.userId}
      >
        <GrowthContent />
      </GrowthProvider>
    </GrowthErrorBoundary>
  );
};

export default GrowthApp;
